import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { SignUpFormData } from '../forms.types';
import InputUi, { InputType } from '../../ui/input.ui';
import logo from '../../media/logo.png';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import styles from './signup.module.css';

type SignUpProps = {
  formData: SignUpFormData;
  error: string;
  passwordErrors: string[];
  loading: boolean;
  onSubmit: (e: React.FormEvent, acceptedTerms: boolean) => void;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function SignUpUi(props: SignUpProps) {
  const {
    formData,
    error,
    passwordErrors,
    loading,
    onSubmit,
    onChange,
  } = props;

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleBackToHome = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (acceptedTerms) {
      setTermsError('');
    }
  }, [acceptedTerms]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!acceptedTerms) {
      setTermsError('You must agree to our Privacy Policy and Terms and Conditions to continue.');
      return;
    }
    onSubmit(e, acceptedTerms);
  };

  return (
    <div className={styles.wrapper}>
      <img
        src={logo}
        alt="Foundermatcha Logo"
        className={styles.logo}
      />
      <h2 className={styles.title}>Create New Account</h2>
      <form onSubmit={handleSubmit} className={styles.form}>

        <div className={styles.formField}>
          <label htmlFor={'email'} className={styles.label}>
            Email: *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={onChange}
            required
            className={styles.input}
          />
        </div>

        <div className={styles.formField}>
          <label className={styles.label}>
            Password: *
          </label>
          <div className={styles.passwordContainer}>
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={onChange}
              required
              placeholder="At least 8 characters"
              className={styles.input}
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword
                ? <VisibilityOffIcon fontSize="small" style={{ color: 'var(--primary-color)' }} />
                : <VisibilityIcon fontSize="small" style={{ color: 'var(--primary-color)' }} />
              }
            </button>
          </div>
        </div>

        {passwordErrors.length > 0 && (
          <div className={styles.passwordErrors}>
            <p className={styles.passwordErrorsTitle}>Password requirements:</p>
            <ul className={styles.passwordErrorsList}>
              {passwordErrors.map((error, index) => (
                <li key={index} className={styles.passwordErrorItem}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={styles.formField}>
          <label className={styles.label}>
            Confirm Password: *
          </label>
          <div className={styles.passwordContainer}>
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={onChange}
              required
              className={styles.input}
            />
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword
                ? <VisibilityOffIcon fontSize="small" style={{ color: 'var(--primary-color)' }} />
                : <VisibilityIcon fontSize="small" style={{ color: 'var(--primary-color)' }} />
              }
            </button>
          </div>
        </div>

        <div className={styles.termsWrapper}>
          <InputUi
            type={InputType.checkbox}
            id={'acceptedTerms'}
            value={acceptedTerms ? 'true' : 'false'}
            className={styles.checkbox}
            onChange={(val) => setAcceptedTerms(!acceptedTerms)}
          />
          <div className={styles.termsText}>
            I consent to the terms of the{' '}
            <a href="/terms-and-conditions.html"target="_blank" rel="noopener noreferrer" className={styles.termsLink}>
              Terms and Conditions
            </a>.
          </div>
        </div>

        <div className={styles.errorContainer}>
          {termsError && (
            <div className={styles.error}>{termsError}</div>
          )}
          {error && (
            <div className={styles.error}>{error}</div>
          )}
        </div>

        <button
          type="submit"
          disabled={loading}
          className={styles.submitButton}
        >
          {loading ? 'Creating Account...' : 'Sign Up'}
        </button>
      </form>

      <div className={styles.message}>
        <p className={styles.messageText}>
          Already have an account?{' '}
          <Link to="/login" className={styles.messageLink}>Login here</Link>
        </p>
        <button
          onClick={handleBackToHome}
          className={styles.backHomeButton}
          type="button"
        >
          ← Back to Home
        </button>
      </div>
    </div>
  );
};

export default SignUpUi;
